/**
 * What the consent documents share (lib/plotConsentDocument builds them): escaping, the
 * organisation's letterhead, the OTP stamp's facts; and the helpers that download or print an HTML
 * page (evidence packs, farmer statements).
 *
 * The template-merged consent letter that lived here (consent_templates, {{merge fields}}) was
 * retired on 2026-09-28, when every consent became one two-language document.
 */
export const esc = (value: string) =>
  value.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!));

/** The organisation's letterhead (migration 0059). The logo is a data: URI so a stored or printed
 *  form carries it for good - a signed storage URL would stop working within minutes. */
export interface Letterhead {
  display_name: string | null;
  logoDataUri?: string | null;
  address_lines?: string | null;
  contact_line?: string | null;
  registration_line?: string | null;
  footer_text?: string | null;
  accent_colour?: string | null;
  /** The office nearest the farmer: "Project office (Sirsi): ... · phone". */
  office_line?: string | null;
}

export interface OtpSignature {
  /** Where the one code went: channel and masked destination, e.g. { channel: 'sms', destination: '******4001' }. */
  channels: Array<{ channel: string; destination: string }>;
  sentAt?: string | null;
  verifiedAt: string;
  verifiedBy?: string | null;
  reference: string;
}

export function letterheadHtml(letterhead: Letterhead | null | undefined, fallbackName: string): string {
  const accent = /^#[0-9a-fA-F]{6}$/.test(letterhead?.accent_colour ?? '') ? letterhead!.accent_colour! : '#1f3864';
  const name = letterhead?.display_name || fallbackName;
  const lines = [letterhead?.address_lines, letterhead?.contact_line, letterhead?.office_line, letterhead?.registration_line]
    .filter((line): line is string => Boolean(line && line.trim()))
    .map((line) => esc(line).replace(/\n/g, '<br>'));
  return `<header class="lh" style="border-bottom:3px solid ${accent}">
${letterhead?.logoDataUri ? `<img class="lh-logo" src="${letterhead.logoDataUri}" alt="">` : ''}
<div class="lh-text"><div class="lh-name" style="color:${accent}">${esc(name)}</div>${lines.map((line) => `<div>${line}</div>`).join('')}</div>
</header>`;
}

/** Download a string as a file. */
export function downloadText(content: string, fileName: string, type = 'text/html') {
  const url = URL.createObjectURL(new Blob([content], { type: `${type};charset=utf-8` }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName.replace(/[\\/:*?"<>|]+/g, '-');
  anchor.click();
  URL.revokeObjectURL(url);
}

/** Open the page in a new window to print or save as PDF. */
export function printHtml(content: string): boolean {
  const win = window.open('', '_blank', 'width=860,height=1000');
  if (!win) return false;
  win.document.write(content);
  win.document.close();
  return true;
}
